import {
  Box,
  Button,
  HStack,
  IconButton,
  NativeSelect,
  Text,
} from '@chakra-ui/react'
import { memo, type ReactNode, useId, useMemo } from 'react'

import type { TranslateFn } from '../types'
import { text } from '../utils/createTranslator'

export interface ListToolbarProps {
  /** Current page size */
  pageSize: number | 'All'
  /** Callback when page size changes */
  onPageSizeChange: (size: number | 'All') => void
  /** Current page number (1-indexed) */
  currentPage: number
  /** Total number of items */
  totalCount: number
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /** Whether data is loading */
  isLoading: boolean
  /** Whether to show the toolbar */
  showToolbar: boolean
  /**
   * When true, shows a range summary (e.g. `1—10 of 461`) between the page-size
   * control and pagination. Hidden when there is nothing to show.
   */
  showRecordRange?: boolean
  /**
   * Extra summary rendered beside the page-size control, for lists whose
   * count copy is not the shared record range.
   */
  summary?: ReactNode
  /** Available page size options (defaults to [25, 50, 100, 250, 500]) */
  pageSizeOptions?: number[]
  /** Whether to include "All" as a page size option (default true) */
  showAllOption?: boolean
  /** Hide "All" when totalCount exceeds this value (default 500) */
  maxAllThreshold?: number
  /** Optional translation function for i18n */
  t?: TranslateFn
  /** Prefix for pagination i18n keys (e.g. 'switchboard.list.pagination'). Defaults to 'pagination'. */
  i18nPrefix?: string
  /** Visual size of the toolbar. 'sm' uses smaller padding and controls. */
  size?: 'default' | 'sm'
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500]
const DEFAULT_MAX_ALL_THRESHOLD = 500

/** Visible row span for the current page (1-based indices). */
export function computeListRecordRange(
  currentPage: number,
  pageSize: number | 'All',
  totalCount: number,
): { start: number; end: number; total: number } | null {
  if (totalCount < 0) return null
  if (totalCount === 0) {
    return { start: 0, end: 0, total: 0 }
  }
  if (pageSize === 'All') {
    return { start: 1, end: totalCount, total: totalCount }
  }
  const ps = pageSize
  const start = (currentPage - 1) * ps + 1
  const end = Math.min(currentPage * ps, totalCount)
  return { start, end, total: totalCount }
}

type EllipsisItem = { type: 'ellipsis'; targetPage: number }
type PageItem = number | EllipsisItem

function generatePageNumbers(
  currentPage: number,
  totalPages: number,
): PageItem[] {
  const pages: PageItem[] = []
  const JUMP_SIZE = 5

  if (totalPages <= 4) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
    return pages
  }

  const nearStart = currentPage <= 3
  const nearEnd = currentPage >= totalPages - 2

  if (nearStart) {
    pages.push(1)
    pages.push(2)
    pages.push(3)
    const targetPage = Math.min(totalPages - 1, 3 + JUMP_SIZE)
    pages.push({ type: 'ellipsis', targetPage })
    pages.push(totalPages)
  } else if (nearEnd) {
    pages.push(1)
    const targetPage = Math.max(2, totalPages - 2 - JUMP_SIZE)
    pages.push({ type: 'ellipsis', targetPage })
    pages.push(totalPages - 2)
    pages.push(totalPages - 1)
    pages.push(totalPages)
  } else {
    pages.push(1)
    const targetPage = Math.max(2, currentPage - JUMP_SIZE)
    pages.push({ type: 'ellipsis', targetPage })
    pages.push(currentPage)
    pages.push(currentPage + 1)
    pages.push(totalPages)
  }

  return pages
}

function isEllipsis(item: PageItem): item is EllipsisItem {
  return typeof item === 'object' && item.type === 'ellipsis'
}

/**
 * Fills `{{var}}` tokens that a translator left in place. Some hosts return
 * the catalog string without applying the options `ListToolbar` passed.
 */
const interpolateReturnedTemplate = (
  template: string,
  opts: Record<string, unknown>,
): string =>
  template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = opts[name]
    return value !== undefined && value !== null ? String(value) : match
  })

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EllipsisIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M10.0002 10.8327C10.4604 10.8327 10.8335 10.4596 10.8335 9.99935C10.8335 9.53911 10.4604 9.16602 10.0002 9.16602C9.53993 9.16602 9.16683 9.53911 9.16683 9.99935C9.16683 10.4596 9.53993 10.8327 10.0002 10.8327Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.8335 10.8327C16.2937 10.8327 16.6668 10.4596 16.6668 9.99935C16.6668 9.53911 16.2937 9.16602 15.8335 9.16602C15.3733 9.16602 15.0002 9.53911 15.0002 9.99935C15.0002 10.4596 15.3733 10.8327 15.8335 10.8327Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.16683 10.8327C4.62707 10.8327 5.00016 10.4596 5.00016 9.99935C5.00016 9.53911 4.62707 9.16602 4.16683 9.16602C3.70659 9.16602 3.3335 9.53911 3.3335 9.99935C3.3335 10.4596 3.70659 10.8327 4.16683 10.8327Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PAGINATION_SIZES = {
  default: { cell: 40, px: 4 },
  sm: { cell: 28, px: 2 },
} as const

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  isLoading: boolean
  t?: TranslateFn
  i18nPrefix: string
  size?: 'default' | 'sm'
}

const PaginationControls = memo<PaginationControlsProps>(
  ({
    currentPage,
    totalPages,
    onPageChange,
    isLoading,
    t,
    i18nPrefix: p,
    size = 'default',
  }) => {
    const pages = useMemo(
      () => generatePageNumbers(currentPage, totalPages),
      [currentPage, totalPages],
    )

    const { cell, px } = PAGINATION_SIZES[size]
    const cellPx = `${cell}px`

    const cellStyles = {
      display: 'flex',
      h: cellPx,
      minW: cellPx,
      px,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      borderRadius: 0,
      border: '1px solid',
      borderColor: 'border.default',
      marginLeft: '-1px',
      fontSize: size === 'sm' ? 'xs' : 'sm',
    }

    const activeStyles = {
      ...cellStyles,
      bg: 'brand.solid',
      color: 'brand.contrast',
      borderColor: 'brand.solid',
      _hover: { bg: 'brand.solid', borderColor: 'brand.solid' },
    }

    const chevronStyles = {
      display: 'flex',
      w: cellPx,
      h: cellPx,
      minW: cellPx,
      p: '2px',
      px,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      border: '1px solid',
      borderColor: 'border.default',
      marginLeft: '-1px',
    }

    if (totalPages <= 1) {
      return (
        <HStack gap="0" alignItems="center" borderRadius={0}>
          <IconButton
            variant="ghost"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={true}
            aria-label={text(t, `${p}.previousPage`, 'Previous page')}
            {...chevronStyles}
            borderLeftRadius={'sm'}
            marginLeft={0}
            _hover={{ bg: 'bg.subtle' }}
          >
            <ChevronLeft />
          </IconButton>

          <Button
            {...activeStyles}
            fontWeight="medium"
            disabled={isLoading}
            aria-current="page"
          >
            1
          </Button>

          <IconButton
            variant="ghost"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={true}
            aria-label={text(t, `${p}.nextPage`, 'Next page')}
            {...chevronStyles}
            borderRightRadius="sm"
            _hover={{ bg: 'bg.subtle' }}
          >
            <ChevronRight />
          </IconButton>
        </HStack>
      )
    }

    return (
      <HStack gap="0" alignItems="center" borderRadius="sm" overflow="hidden">
        <IconButton
          variant="ghost"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1 || isLoading}
          aria-label={text(t, `${p}.previousPage`, 'Previous page')}
          {...chevronStyles}
          marginLeft={0}
          _hover={{ bg: 'bg.subtle' }}
          borderLeftRadius="sm"
        >
          <ChevronLeft />
        </IconButton>

        {pages.map((page, index) => {
          if (isEllipsis(page)) {
            return (
              <IconButton
                key={`ellipsis-${index}`}
                variant="ghost"
                onClick={() => onPageChange(page.targetPage)}
                disabled={isLoading}
                aria-label={text(
                  t,
                  `${p}.jumpToPage`,
                  `Jump to page ${page.targetPage}`,
                  { page: page.targetPage },
                )}
                {...cellStyles}
                px={2.5}
                color="fg.default"
                _hover={{ bg: 'bg.subtle' }}
              >
                <EllipsisIcon />
              </IconButton>
            )
          }

          const isActive = page === currentPage
          return (
            <Button
              key={page}
              variant="ghost"
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              fontWeight="medium"
              aria-current={isActive ? 'page' : undefined}
              {...(isActive
                ? activeStyles
                : {
                    ...cellStyles,
                    bg: 'transparent',
                    color: 'fg.default',
                    _hover: { bg: 'bg.subtle' },
                  })}
            >
              {page}
            </Button>
          )
        })}

        <IconButton
          variant="ghost"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages || isLoading}
          aria-label={text(t, `${p}.nextPage`, 'Next page')}
          {...chevronStyles}
          _hover={{ bg: 'bg.subtle' }}
          borderRightRadius="sm"
        >
          <ChevronRight />
        </IconButton>
      </HStack>
    )
  },
)

PaginationControls.displayName = 'PaginationControls'

/**
 * Reusable list toolbar with page-size selector and pagination controls.
 */
export const ListToolbar = memo<ListToolbarProps>(
  ({
    pageSize,
    onPageSizeChange,
    currentPage,
    totalCount,
    onPageChange,
    isLoading,
    showToolbar,
    showRecordRange = false,
    summary,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    showAllOption = true,
    maxAllThreshold = DEFAULT_MAX_ALL_THRESHOLD,
    t,
    i18nPrefix: p = 'pagination',
    size = 'default',
  }) => {
    const resultsPerPageLabelId = useId()

    if (!showToolbar) return null

    const canShowAll = showAllOption && totalCount <= maxAllThreshold
    const totalPages =
      pageSize === 'All' ? 1 : Math.ceil(totalCount / (pageSize as number))
    const isSm = size === 'sm'

    const recordRange = showRecordRange
      ? computeListRecordRange(currentPage, pageSize, totalCount)
      : null

    const recordRangeLabel = (() => {
      if (!recordRange) return null
      if (recordRange.total === 0) {
        return text(t, `${p}.recordRangeEmpty`, '0 of 0')
      }
      const values = {
        start: recordRange.start,
        end: recordRange.end,
        total: recordRange.total,
      }
      return interpolateReturnedTemplate(
        text(t, `${p}.recordRange`, '{{start}}—{{end}} of {{total}}', values),
        values,
      )
    })()

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        pt={isSm ? 2 : 4}
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <HStack gap={2} alignItems="center" flexShrink={0}>
          <Text
            id={resultsPerPageLabelId}
            fontSize={isSm ? 'xs' : 'sm'}
            color="fg.subtle"
          >
            {text(t, `${p}.resultsPerPage`, 'Results per page:')}
          </Text>
          <NativeSelect.Root size="sm" w={isSm ? '60px' : '75px'}>
            <NativeSelect.Field
              aria-labelledby={resultsPerPageLabelId}
              value={pageSize.toString()}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const newSize =
                  e.target.value === 'All'
                    ? 'All'
                    : parseInt(e.target.value, 10)
                onPageSizeChange(newSize)
              }}
              bg="bg.subtle"
              borderColor="border.default"
              borderRadius="lg"
              fontSize={isSm ? 'xs' : 'sm'}
              fontWeight="medium"
              color="fg.default"
              pl={2}
              _hover={{ borderColor: 'border.emphasized' }}
              _focus={{
                borderColor: 'brand.solid',
                boxShadow: '0 0 0 1px var(--chakra-colors-brand-solid)',
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
              {canShowAll && (
                <option value="All">{text(t, `${p}.all`, 'All')}</option>
              )}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {summary}
        </HStack>

        {showRecordRange && recordRangeLabel && pageSize !== 'All' && (
          <Box flex="1" display="flex" justifyContent="center" minW="0" px={2}>
            <Text
              fontSize={isSm ? 'xs' : 'sm'}
              color="fg.subtle"
              whiteSpace="nowrap"
              aria-live="polite"
            >
              {recordRangeLabel}
            </Text>
          </Box>
        )}

        {pageSize !== 'All' && (
          <Box flexShrink={0}>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              isLoading={isLoading}
              t={t}
              i18nPrefix={p}
              size={size}
            />
          </Box>
        )}

        {showRecordRange && recordRangeLabel && pageSize === 'All' && (
          <Text
            flexShrink={0}
            fontSize={isSm ? 'xs' : 'sm'}
            color="fg.subtle"
            whiteSpace="nowrap"
            ml={{ base: 0, md: 'auto' }}
            aria-live="polite"
          >
            {recordRangeLabel}
          </Text>
        )}
      </Box>
    )
  },
)

ListToolbar.displayName = 'ListToolbar'
